import { Account } from '@/client/accounts';
import { Course } from '@/client/courses';
import { LoadingStatuses } from '@/common/constants';
import { AccountSelectInput } from '@/components/form/selectors';
import { AppButton } from '@/ui/buttons';
import { FlexContainer } from '@/ui/styled-ui';
import {
  CloneToCurrentAccountIcon,
  CloneToDifferentAccountIcon,
} from '@/utils/helpers/icons.helper';
import { Dialog } from 'primereact/dialog';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const StyledCloneDialog = styled(Dialog)`
  width: 500px;
`;

const CloneCardsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--semi-medium-padding);
`;

const CloneCardIcon = styled.div`
  width: 38px;
  height: 38px;
  border-radius: var(--box-medium-border-radius);
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--red-dark);
`;

const CloneOptionCard = styled.button`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: var(--semi-medium-padding);
  border: 1px solid var(--gray-dark);
  border-radius: var(--box-border-radius);
  background: var(--beige-main);
  padding: var(--default-padding-small);
  cursor: pointer;
  width: 100%;
  text-align: left;
  font-weight: 600;
  color: var(--black-main);
  line-height: 140% !important;

  &:hover {
    background: var(--gray-pale);
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

type CloneCourseModalProps = {
  visible: boolean;
  course: Course | null;
  currentAccount: Account | null;
  isCloning?: boolean;
  onHide: () => void;
  onClone: (
    targetAccountId: string,
    targetAccountName?: string,
  ) => Promise<void> | void;
};

type Step = 'destination' | 'account';

export const CloneCourseModal: React.FC<CloneCourseModalProps> = ({
  visible,
  course,
  currentAccount,
  isCloning = false,
  onHide,
  onClone,
}) => {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>('destination');
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);

  useEffect(() => {
    if (!visible) return;
    setStep('destination');
    setSelectedAccount(null);
  }, [visible]);

  const title =
    step === 'destination'
      ? t('content.clone.modal.title.destination')
      : t('content.clone.modal.title.account');

  return (
    <StyledCloneDialog
      visible={visible}
      onHide={onHide}
      header={title}
      draggable={false}
      closable={false}
      className="v2"
    >
      {step === 'destination' && (
        <FlexContainer
          direction="column"
          gap={'var(--medium-big-padding)'}
          className="mt-3"
        >
          <CloneCardsRow>
            <CloneOptionCard
              type="button"
              disabled={!currentAccount?.id || isCloning || !course?.id}
              onClick={() =>
                currentAccount?.id
                  ? onClone(currentAccount.id, currentAccount.name)
                  : undefined
              }
            >
              <CloneCardIcon>
                <CloneToCurrentAccountIcon />
              </CloneCardIcon>
              <div>{t('content.clone.modal.option.current')}</div>
            </CloneOptionCard>
            <CloneOptionCard
              type="button"
              disabled={isCloning || !course?.id}
              onClick={() => setStep('account')}
            >
              <CloneCardIcon>
                <CloneToDifferentAccountIcon />
              </CloneCardIcon>
              <strong>{t('content.clone.modal.option.other')}</strong>
            </CloneOptionCard>
          </CloneCardsRow>
          <FlexContainer justify="flex-start">
            <AppButton
              label={t('button.cancel')}
              severity="secondary"
              type="outlined"
              onClick={onHide}
              isDisabled={isCloning}
            />
          </FlexContainer>
        </FlexContainer>
      )}

      {step === 'account' && (
        <FlexContainer
          direction="column"
          gap="var(--medium-padding)"
          className="mt-3"
        >
          <div className="field w-full">
            <label htmlFor="clone-account-selector" className="mb-0">
              {t('content.clone.modal.account.label')}
              <span className="red">*</span>
            </label>
            <AccountSelectInput
              id="clone-account-selector"
              className="w-full mt-2"
              defaultValue={selectedAccount}
              onSelect={(value) => setSelectedAccount(value ?? null)}
              ignoreDemoMode
              isDisabled={isCloning}
            />
          </div>
          <FlexContainer justify="flex-start" gap="var(--small-padding)">
            <AppButton
              label={t('content.clone')}
              onClick={() =>
                selectedAccount?.id
                  ? onClone(selectedAccount.id, selectedAccount.name)
                  : undefined
              }
              isDisabled={!selectedAccount?.id || isCloning}
              state={isCloning ? LoadingStatuses.LOADING : undefined}
            />
            <AppButton
              label={t('button.cancel')}
              severity="secondary"
              type="outlined"
              onClick={onHide}
              isDisabled={isCloning}
            />
          </FlexContainer>
        </FlexContainer>
      )}
    </StyledCloneDialog>
  );
};
